import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { format } from 'date-fns';
import { kebabCase } from 'lodash-es';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import {
  getApprovalsDashboardDocuments,
  getApprovalsDashboardTotals,
  downloadApprovalsDashboardCsv as downloadApprovalsDashboardCsvRequest,
} from '@/api/approvalsDashboard';
import {
  cancelApprovalFlow,
  remindApprovalFlowApprover,
} from '@/api/approvalFlows';
import { handleCSVDownload } from '@/helpers/fileDownload';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilters } from '@/stores/filters/filters';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import {
  APPROVAL_STATUS_OPTIONS,
  getPendingSlots,
} from '@/components/library/approvals/helpers';

const CSV_DOWNLOAD_ENABLED = false;

export const useApprovalsDashboardStore = defineStore('approvalsDashboard', () => {
  const { setLoading } = useAppLoading();
  const { updateFilters, sendFilterRequest, loadFilters } = useFilters();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  const { fields, mergedTempFilters } = storeToRefs(useFilters());
  const { authData } = storeToRefs(useAppAuthData());

  const flows = ref([]);
  const flowCount = ref(0);
  const totals = ref({});
  const textFilter = ref('');
  const selectedStatuses = ref([]);
  const csvDownloadLoading = ref(false);
  const remindingFlowUlid = ref(null);
  const cancellingFlowUlid = ref(null);
  const remindingSlotApproverKey = ref(null);

  const getFlowByUlid = (ulid) => (
    flows.value.find((flow) => flow.ulid === ulid) ?? null
  );

  const getStatusFilterOption = (id) => APPROVAL_STATUS_OPTIONS.find((option) => option.id === id);

  const buildStatusFilterField = (fieldsToUse, newValue) => {
    const existingField = fieldsToUse.find((field) => field.id === 'status');
    const newField = existingField
      ? { ...existingField, value: newValue }
      : {
        id: 'status',
        label: 'Status',
        type: 'flatlist',
        async: false,
        asyncUrl: null,
        position: 'column_1',
        alwaysOn: false,
        options: APPROVAL_STATUS_OPTIONS,
        category: 'basic',
        value: newValue,
      };

    return existingField
      ? fieldsToUse.map((field) => (field.id === 'status' ? newField : field))
      : [...fieldsToUse, newField];
  };

  const setTextFilter = (value) => {
    textFilter.value = value;
  };

  const loadFlows = async ({
    routeQuery,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      const params = {
        ...(routeQuery || {}),
        sort: '-created_at',
        page: page || undefined,
      };

      const {
        items = [], pagination, textFilter: responseTextFilter,
      } = await getApprovalsDashboardDocuments({
        params,
        searchTerm: newSearchTerm,
        selectedFilters,
      });

      flows.value = items;
      flowCount.value = pagination?.totalDocuments ?? 0;
      useAppPagination().updatePagination(pagination);

      if (responseTextFilter?.value !== undefined) {
        setTextFilter(responseTextFilter.value);
      }
    } catch (err) {
      addErrorSnackbar({
        text: `Approvals dashboard load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  const loadTotals = async ({ selectedFilters, routeQuery } = {}) => {
    try {
      const {
        status: _,
        ...filtersWithoutStatus
      } = selectedFilters || {};

      const {
        status: __,
        ...routeQueryWithoutStatus
      } = routeQuery || {};

      const summary = await getApprovalsDashboardTotals({
        selectedFilters: filtersWithoutStatus,
        params: routeQueryWithoutStatus,
        searchTerm: textFilter.value,
      });

      totals.value = summary || {};
    } catch (err) {
      addErrorSnackbar({
        text: `Approvals dashboard totals load error: ${err.message || err}`,
      });

      throw err;
    }
  };

  const loadPage = async (params) => {
    loadFlows(params);
    loadTotals(params);
    loadFilters(params);
  };

  const downloadCsv = async (routeQuery) => {
    if (!CSV_DOWNLOAD_ENABLED) {
      addErrorSnackbar({ text: 'CSV download is not available yet.' });
      return;
    }

    csvDownloadLoading.value = true;

    try {
      const params = {
        ...(routeQuery || {}),
        sort: '-created_at',
      };

      const commaSeparatedString = await downloadApprovalsDashboardCsvRequest({
        params,
        selectedFilters: mergedTempFilters.value,
        searchTerm: textFilter.value,
      });

      const { projectName } = authData.value;
      const today = format(new Date(), READABLE_FILE_NAME_FORMAT);
      const fileTitle = `${kebabCase(projectName.toLowerCase())}-approvals-dashboard-${today}.csv`;

      handleCSVDownload({
        commaSeparatedString,
        autoClick: true,
        title: fileTitle,
      });
    } catch (error) {
      addErrorSnackbar({ text: `Approvals dashboard CSV download: ${error.message}` });
      throw error;
    } finally {
      csvDownloadLoading.value = false;
    }
  };

  const updateSelectedStatuses = (statusIds) => {
    selectedStatuses.value = statusIds;
  };

  const resetSelectedStatus = (router) => {
    if (!selectedStatuses.value?.length) return;

    selectedStatuses.value = [];

    const fieldsToUse = fields.value || [];
    const newFields = buildStatusFilterField(fieldsToUse, []);

    updateFilters({ filters: newFields });
    sendFilterRequest({ router });
  };

  const selectStatus = (statusIds, router) => {
    const currentStatusIds = [...selectedStatuses.value].sort();
    const targetStatusIds = [...statusIds].sort();
    const fieldsToUse = fields.value || [];

    const isSameSelection = currentStatusIds.length === targetStatusIds.length
      && currentStatusIds.every((id, index) => id === targetStatusIds[index]);

    const newIds = isSameSelection ? [] : statusIds;

    selectedStatuses.value = newIds;
    setTextFilter('');

    const newValue = newIds
      .map((id) => getStatusFilterOption(id))
      .filter(Boolean);

    const newFields = buildStatusFilterField(fieldsToUse, newValue);

    updateFilters({ filters: newFields });
    sendFilterRequest({ router });
  };

  const cancelFlow = async (flowUlid) => {
    if (!flowUlid) return;

    cancellingFlowUlid.value = flowUlid;

    try {
      await cancelApprovalFlow({ flowUlid });

      const index = flows.value.findIndex((flow) => flow.ulid === flowUlid);

      if (index !== -1) {
        flows.value[index] = {
          ...flows.value[index],
          status: 'cancelled',
        };
      }

      addSuccessSnackbar({ text: 'Approval flow cancelled.' });
    } catch (error) {
      addErrorSnackbar({
        text: `Failed to cancel approval flow — ${error.message}`,
      });

      throw error;
    } finally {
      cancellingFlowUlid.value = null;
    }
  };

  const remindSlotApprover = async ({ slotUlid, approver }) => {
    if (!slotUlid || !approver?.ulid) return;

    const reminderKey = `${slotUlid}:${approver.ulid}`;
    remindingSlotApproverKey.value = reminderKey;

    try {
      await remindApprovalFlowApprover({
        approvalFlowSlotUlid: slotUlid,
        userUlid: approver.ulid,
      });

      addSuccessSnackbar({ text: `Reminder sent to ${approver.first_name || 'approver'}.` });
    } catch (error) {
      addErrorSnackbar({
        text: `Failed to send reminder — ${error.message}`,
      });

      throw error;
    } finally {
      remindingSlotApproverKey.value = null;
    }
  };

  const remindFlowApprovers = async (flow) => {
    if (!flow?.ulid) return;

    const pendingSlots = getPendingSlots(flow);

    if (!pendingSlots.length) {
      addErrorSnackbar({ text: 'No pending approvers to remind for this flow.' });
      return;
    }

    remindingFlowUlid.value = flow.ulid;

    try {
      const reminders = pendingSlots.flatMap((slot) => (
        (slot.approvers ?? [])
          .filter((approver) => approver.ulid)
          .map((approver) => remindApprovalFlowApprover({
            approvalFlowSlotUlid: slot.ulid,
            userUlid: approver.ulid,
          }))
      ));

      await Promise.all(reminders);

      addSuccessSnackbar({ text: 'Reminder sent to pending approvers.' });
    } catch (error) {
      addErrorSnackbar({
        text: `Failed to send reminder — ${error.message}`,
      });

      throw error;
    } finally {
      remindingFlowUlid.value = null;
    }
  };

  const reset = () => {
    flows.value = [];
    flowCount.value = 0;
    totals.value = {};
    textFilter.value = '';
    selectedStatuses.value = [];
    csvDownloadLoading.value = false;
    remindingFlowUlid.value = null;
    cancellingFlowUlid.value = null;
    remindingSlotApproverKey.value = null;
  };

  return {
    flows,
    flowCount,
    totals,
    textFilter,
    selectedStatuses,
    csvDownloadLoading,
    remindingFlowUlid,
    cancellingFlowUlid,
    remindingSlotApproverKey,
    csvDownloadEnabled: CSV_DOWNLOAD_ENABLED,
    getFlowByUlid,
    setTextFilter,
    loadPage,
    downloadCsv,
    selectStatus,
    resetSelectedStatus,
    updateSelectedStatuses,
    cancelFlow,
    remindSlotApprover,
    remindFlowApprovers,
    reset,
  };
});
